import React, { useState } from 'react'
import Dialog from './Dialog'

// Reusable confirm/warning dialog (modelled on the fertiliser project's Warning): call
// useWarningState in a component, show(title, message, data) to open it, and render <Warning> with an
// onYes handler. The data payload is threaded back to onYes/onNo so the caller can resume its action.
interface WarningInternalState<T> {
    show: boolean;
    title: string;
    message: string;
    data: T;
}

export interface WarningState<T> {
    show: (title: string, message: string, data: T) => void;
    hide: () => void;
    state: WarningInternalState<T>;
}

export function useWarningState<T> (empty: T): WarningState<T> {
    const [state, setState] = useState<WarningInternalState<T>>({
        show: false,
        title: '',
        message: '',
        data: empty
    })

    return {
        show: (title, message, data) => setState({ show: true, title, message, data }),
        hide: () => setState({ show: false, title: '', message: '', data: empty }),
        state
    }
}

interface WarningProps<T> {
    state: WarningState<T>;
    onYes: (data: T) => void;
    onNo?: (data: T) => void;
    yesLabel?: string;
    noLabel?: string;
}

const Warning = <T, >(props: WarningProps<T>) => {
    const state = props.state.state

    return <Dialog
        title={state.title}
        show={state.show}
        setShow={() => props.state.hide()}
        body={
            <div className="p-4 max-w-md text-gray-700">
                {state.message}
            </div>
        }
        footer={
            <>
                <button
                    className="btn btn-sm btn-error mr-1"
                    onClick={() => { props.onNo?.(state.data); props.state.hide() }}>
                    {props.noLabel ?? 'Cancel'}
                </button>
                <button
                    className="btn btn-sm btn-primary"
                    onClick={() => { props.onYes(state.data); props.state.hide() }}>
                    {props.yesLabel ?? 'Continue'}
                </button>
            </>
        }/>
}

export default Warning
